/**
 * Pure date-range model for the Kronos report's Clockify-style picker.
 *
 * No React, no Firestore, no `new Date()` inside helpers — `today` is always
 * injected so every function is deterministic and unit-testable. Monday-first
 * weeks (Altyor is French); reuses the dashboard's boundary helpers.
 */
import { startOfMonth, startOfWeekMonday } from './dashboardAggregations';

export { startOfMonth };

export interface DateRange {
  from: Date;
  to: Date;
}

export type RangePresetId =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'lastWeek'
  | 'pastTwoWeeks'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisYear'
  | 'lastYear'
  | 'toDate';

/** Ordered list driving the preset sidebar + `matchPreset` scan. */
export const PRESET_IDS: RangePresetId[] = [
  'today',
  'yesterday',
  'thisWeek',
  'lastWeek',
  'pastTwoWeeks',
  'thisMonth',
  'lastMonth',
  'thisYear',
  'lastYear',
  'toDate',
];

export function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

export function endOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

export function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Inclusive day count of a range (a 1-day range returns 1). */
export function spanDays(range: DateRange): number {
  const a = startOfDay(range.from).getTime();
  const b = startOfDay(range.to).getTime();
  return Math.round((b - a) / 86_400_000) + 1;
}

export function presetRange(id: RangePresetId, today: Date): DateRange {
  const t = startOfDay(today);
  switch (id) {
    case 'today':
      return { from: startOfDay(t), to: endOfDay(t) };
    case 'yesterday': {
      const y = addDays(t, -1);
      return { from: startOfDay(y), to: endOfDay(y) };
    }
    case 'thisWeek': {
      const mon = startOfWeekMonday(t);
      return { from: mon, to: endOfDay(addDays(mon, 6)) };
    }
    case 'lastWeek': {
      const mon = addDays(startOfWeekMonday(t), -7);
      return { from: mon, to: endOfDay(addDays(mon, 6)) };
    }
    case 'pastTwoWeeks': {
      const mon = addDays(startOfWeekMonday(t), -7);
      return { from: mon, to: endOfDay(addDays(mon, 13)) };
    }
    case 'thisMonth': {
      const start = startOfMonth(t);
      const end = new Date(start.getFullYear(), start.getMonth() + 1, 0, 23, 59, 59, 999);
      return { from: start, to: end };
    }
    case 'lastMonth': {
      const start = new Date(t.getFullYear(), t.getMonth() - 1, 1, 0, 0, 0, 0);
      const end = new Date(t.getFullYear(), t.getMonth(), 0, 23, 59, 59, 999);
      return { from: start, to: end };
    }
    case 'thisYear':
      return {
        from: new Date(t.getFullYear(), 0, 1, 0, 0, 0, 0),
        to: new Date(t.getFullYear(), 11, 31, 23, 59, 59, 999),
      };
    case 'lastYear':
      return {
        from: new Date(t.getFullYear() - 1, 0, 1, 0, 0, 0, 0),
        to: new Date(t.getFullYear() - 1, 11, 31, 23, 59, 59, 999),
      };
    case 'toDate':
      // ponytail: 2020 = debut des donnees Clockify reprises, pas de doc plus ancien
      return { from: new Date(2020, 0, 1, 0, 0, 0, 0), to: endOfDay(t) };
  }
}

/** Which preset (if any) produces `range` for the given `today`. */
export function matchPreset(range: DateRange, today: Date): RangePresetId | null {
  for (const id of PRESET_IDS) {
    const r = presetRange(id, today);
    if (sameDay(r.from, range.from) && sameDay(r.to, range.to)) return id;
  }
  return null;
}

/** Shift the whole range forward/backward by its own inclusive span. */
export function shiftRange(range: DateRange, direction: 1 | -1): DateRange {
  const days = spanDays(range);
  return {
    from: startOfDay(addDays(range.from, direction * days)),
    to: endOfDay(addDays(range.to, direction * days)),
  };
}

export interface CalendarCell {
  date: Date;
  inCurrentMonth: boolean;
}

/** 6×7 = 42 Monday-first cells covering `anchor`'s month, with overflow days. */
export function monthGrid(anchor: Date): CalendarCell[] {
  const first = startOfMonth(anchor);
  const gridStart = startOfWeekMonday(first);
  const cells: CalendarCell[] = [];
  for (let i = 0; i < 42; i++) {
    const date = addDays(gridStart, i);
    cells.push({ date, inCurrentMonth: date.getMonth() === anchor.getMonth() });
  }
  return cells;
}

export function formatRangeLabel(range: DateRange, locale: string): string {
  const opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };
  return `${range.from.toLocaleDateString(locale, opts)} – ${range.to.toLocaleDateString(locale, opts)}`;
}
